'use client';

import { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface DealDetailModalProps {
  deal: {
    id: string;
    title: string;
    category: string;
    status: 'active' | 'monitoring' | 'closing';
    currentValue: string;
    targetROI: string;
    progress: number;
    image: string;
    alt: string;
    startDate: string;
    expectedCompletion: string;
    strategy: string;
    riskLevel: 'low' | 'medium' | 'high';
    highlights: string[];
    description: string;
    metrics: Array<{
      label: string;
      value: string;
      icon: string;
    }>;
    updates: Array<{
      date: string;
      title: string;
      description: string;
    }>;
  };
  isOpen: boolean;
  onClose: () => void;
}

const DealDetailModal = ({ deal, isOpen, onClose }: DealDetailModalProps) => {
  const { t } = useLanguage();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'monitoring':
        return 'bg-blue-100 text-blue-800';
      case 'closing':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-card rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-heading font-bold text-foreground">{t('modal.dealDetails')}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
            aria-label={t('modal.closeModal')}
          >
            <Icon name="XMarkIcon" size={24} variant="outline" />
          </button>
        </div>

        <div className="p-6">
          <div className="relative h-64 rounded-lg overflow-hidden mb-6">
            <AppImage
              src={deal.image}
              alt={deal.alt}
              width={800}
              height={400}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 right-4">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(deal.status)}`}>
                {deal.status.charAt(0).toUpperCase() + deal.status.slice(1)}
              </span>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-3xl font-heading font-bold text-foreground mb-2">{deal.title}</h3>
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <span className="flex items-center space-x-1">
                <Icon name="TagIcon" size={16} variant="outline" />
                <span>{deal.category}</span>
              </span>
              <span className="flex items-center space-x-1">
                <Icon name="ChartBarIcon" size={16} variant="outline" />
                <span>{deal.strategy} Strategy</span>
              </span>
              <span className="flex items-center space-x-1">
                <Icon name="ShieldCheckIcon" size={16} variant="outline" />
                <span>{deal.riskLevel.charAt(0).toUpperCase() + deal.riskLevel.slice(1)} Risk</span>
              </span>
            </div>
          </div>

          <div className="mb-6">
            <p className="text-foreground leading-relaxed">{deal.description}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {deal.metrics.map((metric, index) => (
              <div key={index} className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name={metric.icon as any} size={20} variant="outline" className="text-accent" />
                  <span className="text-xs text-muted-foreground">{metric.label}</span>
                </div>
                <p className="text-xl font-heading font-bold text-foreground">{metric.value}</p>
              </div>
            ))}
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-muted-foreground">Investment Progress</span>
              <span className="text-sm font-semibold text-foreground">{deal.progress}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-3">
              <div
                className="bg-accent rounded-full h-3 transition-all duration-300"
                style={{ width: `${deal.progress}%` }}
              ></div>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-heading font-bold text-foreground mb-3">{t('common.keyHighlights')}</h4>
            <ul className="space-y-2">
              {deal.highlights.map((highlight, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{highlight}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-heading font-bold text-foreground mb-3">{t('modal.recentUpdates')}</h4>
            <div className="space-y-4">
              {deal.updates.map((update, index) => (
                <div key={index} className="border-l-2 border-accent pl-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="CalendarIcon" size={16} variant="outline" className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{update.date}</span>
                  </div>
                  <h5 className="font-semibold text-foreground mb-1">{update.title}</h5>
                  <p className="text-sm text-muted-foreground">{update.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between pt-6 border-t border-border">
            <div>
              <p className="text-sm text-muted-foreground">Started</p>
              <p className="font-semibold text-foreground">{deal.startDate}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Expected Completion</p>
              <p className="font-semibold text-foreground">{deal.expectedCompletion}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Target ROI</p>
              <p className="font-semibold text-green-600 text-xl">{deal.targetROI}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealDetailModal;